import { Col } from 'antd';
import { IconStatsCardComponent } from 'baseComponents/IconStatsCardComponent';
import { TextComponent } from 'baseComponents/TextComponent';
import React from 'react';
import 'screens/CampaignResultsScreen/campaignResultsScreen.scss';
import { MessagseSentIcon } from 'assets/icons/MessagesSent';
import MessageSentIcon from 'assets/icons/messageSentIcon.svg';
import UniqueRecipientsIcon from 'assets/icons/uniqueRecipientsIcon.svg';
import TotalRevenueIcon from 'assets/icons/totalRevenueIcon.svg';
import ConversionRateIcon from 'assets/icons/conversionRateIcon.svg';
import { ResultsTableElementComponent } from 'baseComponents/ResultsTableElementComponent';
import { ScreenWrapperComponent } from 'components/layouts/ScreenWrapperComponent';
import { useParams } from 'react-router-dom';
import { useGetCampaignAnalyticsById } from 'hooks/useGetCampaignAnalyticsById';
import { FullScreenLoaderComponent } from 'baseComponents/FullScreenLoaderComponent';
import { ResultPage } from 'components/ResultPage';
import dayjs from 'dayjs';
import { CurrentAudienceIcon } from 'assets/icons/currentAudienceIcon';
import { CalendarIcon } from 'assets/icons/calendarIcon';
import { LinkComponent } from 'baseComponents/LinkComponent';
import { AudienceDetailsModal } from './components/AudienceDetailsModal.tsx';

export const CampaignResultsScreen: React.FC = () => {
  const [audienceModalOpen, setAudienceModalOpen] = React.useState(false);
  let { id } = useParams();

  const { data, isLoading, isRefetching, isError } =
    useGetCampaignAnalyticsById({ campaignId: id || '' });

  if (isLoading || isRefetching) {
    return <FullScreenLoaderComponent spinning />;
  }

  if (isError) {
    return <ResultPage />;
  }

  const isPushCampaign = data?.channel === 'push';

  if (data) {
    const campaignDetailsData = [
      {
        statsValue: data.sends ? data.sends.toString() : '0',
        title: isPushCampaign ? 'Push sent' : 'Messages sent',
        iconType: MessageSentIcon,
      },
      {
        statsValue: data.confirmed_deliveries
          ? data.confirmed_deliveries.toString()
          : '0',
        title: isPushCampaign ? 'Push delivered' : 'Confirmed deliveries',
        iconType: UniqueRecipientsIcon,
      },
      {
        statsValue: `${
          data.conversion_rate
            ? data.conversion_rate.toFixed(2).toString()
            : '0'
        }%`,
        title: 'Conversion rate',
        iconType: ConversionRateIcon,
      },
      {
        statsValue: `£${
          data.total_revenue ? data.total_revenue.toFixed(2).toString() : '0'
        }`,
        title: 'Total revenue *',
        iconType: TotalRevenueIcon,
      },
    ];
    return (
      <ScreenWrapperComponent>
        <Col
          lg={{ pull: 4, push: 4, span: 16 }}
          md={{ pull: 1, push: 1, span: 22 }}
        >
          <div className='results-title-wrapper'>
            <TextComponent size='table-title-size' text={data.campaign.name} />
            <div className='date-wrapper'>
              <div className='icon-wrapper'>
                <CalendarIcon />
              </div>
              <TextComponent
                text={dayjs(data.campaign.scheduled).format('YYYY-MM-DD HH:mm')}
              />
            </div>
            <div className='date-wrapper'>
              <div className='icon-wrapper'>
                <CurrentAudienceIcon className='icon' />
              </div>
              <TextComponent text='Audience: ' />
              <LinkComponent
                label={
                  data.audience.audience_name || 'No audience name provided'
                }
                onClick={() => setAudienceModalOpen(true)}
              />
            </div>
            <div className='date-wrapper'>
              <div className='icon-wrapper'>
                <MessagseSentIcon />
              </div>
              <TextComponent
                text={`Channel: ${isPushCampaign ? 'Push' : 'SMS'}`}
              />
            </div>
          </div>
          <div className='results-details-wrapper'>
            <TextComponent size='extra-large' text={'Campaign Details'} />
            <div className='details-row'>
              {campaignDetailsData.map((s) => (
                <div className='icon-stats-wrapper'>
                  <IconStatsCardComponent
                    key={s.title}
                    statsValue={s.statsValue || ''}
                    title={s.title}
                    iconType={s.iconType}
                  />
                </div>
              ))}
            </div>
          </div>
          <div className='performanceWrapper'>
            <TextComponent
              size='extra-large'
              text={isPushCampaign ? 'Push Performance' : 'SMS Performance'}
            />
            <div className='performance-table-wrapper'>
              <div className='performance-table-wrapper-row'>
                <ResultsTableElementComponent
                  title='Unique recipients'
                  value={data.unique_recipients?.toString() || '0'}
                />
                <ResultsTableElementComponent
                  title='Sends'
                  value={data.sends?.toString() || '0'}
                />
                <ResultsTableElementComponent
                  title='Confirmed deliveries'
                  value={data.confirmed_deliveries?.toString() || '0'}
                />
              </div>
              <div className='performance-table-wrapper-row'>
                <ResultsTableElementComponent
                  title='Rejections'
                  value={data.rejections?.toString() || '0'}
                />
                <ResultsTableElementComponent
                  title='Revenue *'
                  value={`£${data.total_revenue?.toString() || '0'}`}
                />
                <ResultsTableElementComponent
                  title='Transactions'
                  value={data.primary_conversions?.toString() || '0'}
                />
                {isPushCampaign && (
                  <ResultsTableElementComponent
                    title='Clicked / Open'
                    value={`${data.clicked?.toString() || '0'} / ${data.open?.toString() || '0'}`}
                  />
                )}
              </div>
            </div>
            <p className='delay-info'>
              Campaign Analytics might take up to 3 days to be fully updated.
              <br />
              * Push revenue includes purchases made within 1 day of receiving the notification.
            </p>
          </div>
        </Col>
        <AudienceDetailsModal
          isModalOpen={audienceModalOpen}
          closeModal={() => setAudienceModalOpen(false)}
          audienceName={data.audience.audience_name}
          audienceFilters={data.audience.audience_filters}
          audienceStores={data.audience.audience_stores}
        />
      </ScreenWrapperComponent>
    );
  }

  return <></>;
};
